import {
  cloneElement,
  useMemo,
  type ComponentProps,
  type ReactElement,
} from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { type Media } from "@tudigo-monorepo/core-tudigo-api-models";
import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import {
  getResizedImage,
  getYouTubeThumbnail,
} from "@tudigo-monorepo/core-tudigo-utils";
import {
  ButtonRebrand,
  ButtonRebrandProps,
} from "@tudigo-monorepo/web-tudigo-components";

import { MediaSliderProvider, useMediaSlider } from "./media-slider.context";

const MediaSliderContent = ({ children }: ComponentProps<"div">) => {
  const { pauseAutoplay, resumeAutoplay } = useMediaSlider();

  return (
    <div
      className="relative flex w-full flex-col gap-4 lg:flex-row"
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
    >
      {children}
    </div>
  );
};

const MediaSliderItem = ({
  slide,
  className,
  children,
}: ComponentProps<"div"> & {
  slide: number;
  children?: ReactElement<{ onPlay?: () => void; onPause?: () => void }>;
}) => {
  const { currentSlide, pauseAutoplay, resumeAutoplay } = useMediaSlider();

  return (
    <div
      data-slide-item=""
      className={cn(
        "absolute flex w-full transition-opacity",
        slide !== currentSlide && "z-media-slider-item opacity-0",
        slide === currentSlide && "z-media-slider-current-item opacity-100",
        className,
      )}
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
    >
      {children &&
        cloneElement(children, {
          onPlay: pauseAutoplay,
          onPause: resumeAutoplay,
        })}
    </div>
  );
};

const MediaSliderThumbMenu = ({
  className,
  children,
}: ComponentProps<"aside">) => {
  const { thumbs } = useMediaSlider();

  if (!thumbs.length) {
    return null;
  }

  return (
    <aside
      className={cn(
        "hidden flex-col gap-y-6 rounded-lg p-4 lg:flex",
        className,
      )}
    >
      {children}
    </aside>
  );
};

const MediaSliderThumbItem = ({
  slide,
  className,
  media,
}: {
  slide: number;
  className?: string;
  media?: Media;
}) => {
  const { thumbs, setCurrentSlide } = useMediaSlider();

  const thumbUrl = useMemo(() => {
    if (!media) {
      return;
    }
    if (media.type === "video") {
      const url = getYouTubeThumbnail(media.data.url);

      return url.replace("/0.jpg", "/mqdefault.jpg");
    }
    if (media.type === "image") {
      return (
        getResizedImage(media.data, {
          requestedHeight: 80,
          requestedWidth: 80,
          requestedMode: "resized",
        }).url ?? undefined
      );
    }
  }, [media]);

  if (!media) {
    return null;
  }

  return (
    <ButtonRebrand
      onClick={() => setCurrentSlide(thumbs[slide])}
      variant="ghost"
      className="h-20 w-20 p-0 opacity-40 transition-opacity hover:opacity-100"
    >
      <img
        src={thumbUrl}
        alt="Project media thumbnail"
        className={cn(
          "flex h-full w-full overflow-auto rounded-lg object-cover object-center",
          className,
        )}
      />
    </ButtonRebrand>
  );
};

const MediaSliderControls = ({
  className,
  variant = "ghost",
}: ComponentProps<"div"> & { variant?: ButtonRebrandProps["variant"] }) => {
  const { currentSlide, nbSlides, setCurrentSlide } = useMediaSlider();

  if (nbSlides < 2) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex h-[72px] items-center gap-x-4 rounded-md bg-white px-1",
        className,
      )}
    >
      <ButtonRebrand
        onClick={() => setCurrentSlide(currentSlide - 1)}
        disabled={currentSlide === 0}
        variant={variant}
        className="h-14 w-14 rounded-full"
        title="Précédent"
      >
        <ChevronLeftIcon className="h-6 w-6 text-neutral-900" />
      </ButtonRebrand>
      <span className="text-black-primary min-w-5 text-xs font-semibold">
        {currentSlide + 1}/{nbSlides}
      </span>
      <ButtonRebrand
        onClick={() => setCurrentSlide(currentSlide + 1)}
        disabled={currentSlide === nbSlides - 1}
        variant={variant}
        className="h-14 w-14 rounded-full"
        title="Suivant"
      >
        <ChevronRightIcon className="h-6 w-6 text-neutral-900" />
      </ButtonRebrand>
    </div>
  );
};

const MediaSlider = ({
  className,
  children,
  defaultSlide = 0,
  onSlideChange,
  autoplay = false,
  ...props
}: ComponentProps<"div"> & {
  defaultSlide?: number;
  onSlideChange?: (slide: number) => void;
  autoplay?: boolean;
}) => {
  return (
    <MediaSliderProvider
      defaultSlide={defaultSlide}
      onSlideChange={onSlideChange}
      autoplay={autoplay}
    >
      <div className={cn("flex w-full gap-4", className)} {...props}>
        {children}
      </div>
    </MediaSliderProvider>
  );
};

export {
  MediaSlider,
  MediaSliderContent,
  MediaSliderItem,
  MediaSliderControls,
  MediaSliderThumbItem,
  MediaSliderThumbMenu,
};
